import { ActionIcon, Box, Group, Stack, Text, useComputedColorScheme } from '@mantine/core';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconCopy,
  IconExternalLink,
  IconPhone,
  IconRobot,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';

/**
 * Approximate WhatsApp message-bubble preview. Not a pixel-perfect copy —
 * just close enough that the vendor can see what the recipient will see
 * before submitting for review.
 */

export interface PreviewTemplate {
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  header?: {
    format: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'GIF' | 'DOCUMENT' | 'LOCATION';
    text?: string;
    mediaPreviewUrl?: string;
  };
  bodyText: string;
  /** Fill {{1}}/{{2}} or {{name}} with these sample values. */
  bodyExamples?: string[];
  bodyVariableNames?: string[];
  parameterFormat?: 'POSITIONAL' | 'NAMED';
  footerText?: string;
  buttons?: Array<{
    type: string;
    text?: string;
    url?: string;
    phone_number?: string;
    copyCodeExample?: string;
  }>;
  /** Limited-Time Offer banner (MARKETING). */
  limitedTimeOffer?: { text: string; hasExpiration: boolean };
  /** Carousel cards — rendered as an interactive scroller (◀ ▶ + dots). */
  carouselCards?: Array<{
    headerFormat: 'IMAGE' | 'VIDEO';
    mediaPreviewUrl?: string | null;
    bodyText?: string;
    buttons?: Array<{ type: string; text?: string }>;
  }>;
  /** AUTHENTICATION-only flags. */
  addSecurityRecommendation?: boolean;
  codeExpirationMinutes?: number | null;
}

export function TemplatePreview({ template }: { template: PreviewTemplate }) {
  const colorScheme = useComputedColorScheme('light');
  const isDark = colorScheme === 'dark';

  const cardCount = template.carouselCards?.length ?? 0;
  const [activeCard, setActiveCard] = useState(0);
  // Keep the active index in range as cards are added / removed.
  useEffect(() => {
    setActiveCard((i) => (cardCount === 0 ? 0 : Math.min(i, cardCount - 1)));
  }, [cardCount]);

  const filledBody =
    template.category === 'AUTHENTICATION'
      ? authBodyPreview(template)
      : substitute(template.bodyText, template);

  return (
    <Box
      p="md"
      style={(t) => ({
        background: isDark ? t.colors.dark[7] : '#e5ddd5',
        borderRadius: 8,
        minHeight: 360,
      })}
    >
      <Box
        p="xs"
        mx="auto"
        maw={320}
        style={(t) => ({
          background: isDark ? t.colors.dark[5] : '#fff',
          borderRadius: 8,
          boxShadow: '0 1px 0.5px rgba(0,0,0,0.13)',
          fontSize: 14,
          lineHeight: 1.4,
        })}
      >
        {/* Header */}
        {template.header?.format === 'TEXT' && template.header.text && (
          <Text fw={700} mb={4}>
            {substitute(template.header.text, template, { headerOnly: true })}
          </Text>
        )}
        {template.header?.format === 'IMAGE' && (
          <Box
            mb={6}
            h={120}
            bg="gray.2"
            style={{ borderRadius: 4, backgroundSize: 'cover', backgroundPosition: 'center' }}
          >
            {template.header.mediaPreviewUrl && (
              <img
                src={template.header.mediaPreviewUrl}
                alt="header"
                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
              />
            )}
          </Box>
        )}
        {(template.header?.format === 'VIDEO' || template.header?.format === 'GIF') &&
          (template.header.mediaPreviewUrl ? (
            <Box mb={6} style={{ borderRadius: 4, overflow: 'hidden' }}>
              <video
                src={template.header.mediaPreviewUrl}
                muted
                loop
                autoPlay
                playsInline
                style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }}
              />
            </Box>
          ) : (
            <Box mb={6} h={120} bg="dark.5" style={{ borderRadius: 4 }}>
              <Text size="xs" c="white" ta="center" mt={50}>
                [{template.header.format.toLowerCase()}]
              </Text>
            </Box>
          ))}
        {template.header?.format === 'DOCUMENT' && (
          <Box mb={6} p={6} bg="gray.1" style={{ borderRadius: 4 }}>
            <Text size="xs">📄 [document attachment]</Text>
          </Box>
        )}
        {template.header?.format === 'LOCATION' && (
          <Box mb={6} h={80} bg="green.1" style={{ borderRadius: 4 }}>
            <Text size="xs" c="dimmed" ta="center" mt={32}>
              📍 [location]
            </Text>
          </Box>
        )}

        {/* Body */}
        <Text style={{ whiteSpace: 'pre-wrap' }}>{filledBody}</Text>

        {/* Limited-Time Offer banner */}
        {template.limitedTimeOffer && template.limitedTimeOffer.text && (
          <Box mt={6} p={6} bg={isDark ? 'dark.4' : 'orange.0'} style={{ borderRadius: 4 }}>
            <Group gap={4}>
              <Text size="xs" fw={600}>
                🎁 {template.limitedTimeOffer.text}
              </Text>
              {template.limitedTimeOffer.hasExpiration && (
                <Text size="xs" c="dimmed">
                  · Offer ends soon
                </Text>
              )}
            </Group>
          </Box>
        )}

        {/* Carousel — interactive: ◀ ▶ + clickable dots move between cards */}
        {template.carouselCards && template.carouselCards.length > 0 && (
          <CarouselPreview
            cards={template.carouselCards}
            active={Math.min(activeCard, template.carouselCards.length - 1)}
            onActiveChange={setActiveCard}
            isDark={isDark}
          />
        )}

        {/* Authentication security recommendation */}
        {template.category === 'AUTHENTICATION' && template.addSecurityRecommendation && (
          <Text size="xs" c="dimmed" mt={4}>
            For your security, do not share this code.
          </Text>
        )}

        {/* Footer */}
        {template.category === 'AUTHENTICATION' &&
          template.codeExpirationMinutes !== undefined &&
          template.codeExpirationMinutes !== null && (
            <Text size="xs" c="dimmed" mt={4}>
              This code expires in {template.codeExpirationMinutes} minutes.
            </Text>
          )}
        {template.footerText && (
          <Text size="xs" c="dimmed" mt={4}>
            {template.footerText}
          </Text>
        )}
      </Box>

      {/* Buttons */}
      {template.buttons && template.buttons.length > 0 && (
        <Stack gap={4} mt={6} maw={320} mx="auto">
          {template.buttons.map((b, i) => (
            <Box
              key={i}
              p={6}
              ta="center"
              style={(t) => ({
                background: isDark ? t.colors.dark[5] : '#fff',
                borderRadius: 4,
                color: t.colors.blue[6],
                fontSize: 13,
                fontWeight: 500,
              })}
            >
              <Group justify="center" gap={4}>
                {iconForButton(b.type)}
                <Text inherit>{b.text || labelForUntitled(b.type)}</Text>
              </Group>
            </Box>
          ))}
        </Stack>
      )}

      {/* AUTHENTICATION OTP button */}
      {template.category === 'AUTHENTICATION' && (
        <Stack gap={4} mt={6} maw={320} mx="auto">
          <Box
            p={6}
            ta="center"
            style={(t) => ({
              background: isDark ? t.colors.dark[5] : '#fff',
              borderRadius: 4,
              color: t.colors.blue[6],
              fontSize: 13,
              fontWeight: 500,
            })}
          >
            <Group justify="center" gap={4}>
              <IconCopy size={14} />
              <Text inherit>Copy code</Text>
            </Group>
          </Box>
        </Stack>
      )}
    </Box>
  );
}

type CarouselCardPreview = NonNullable<PreviewTemplate['carouselCards']>[number];

/** Interactive carousel: renders one card at a time with ◀ ▶ nav + dots. */
function CarouselPreview({
  cards,
  active,
  onActiveChange,
  isDark,
}: {
  cards: CarouselCardPreview[];
  active: number;
  onActiveChange: (i: number) => void;
  isDark: boolean;
}) {
  const card = cards[active]!;
  const go = (delta: number) => {
    const next = active + delta;
    if (next >= 0 && next < cards.length) onActiveChange(next);
  };
  return (
    <Box mt={6}>
      <Group gap={4} wrap="nowrap" align="center">
        <ActionIcon
          size="sm"
          variant="light"
          radius="xl"
          disabled={active === 0}
          onClick={() => go(-1)}
          aria-label="Previous card"
        >
          <IconChevronLeft size={14} />
        </ActionIcon>

        <Box style={{ flex: 1, minWidth: 0 }}>
          <Box p={4} style={{ borderRadius: 4, border: '1px dashed #bbb' }}>
            {card.mediaPreviewUrl ? (
              card.headerFormat === 'IMAGE' ? (
                <img
                  src={card.mediaPreviewUrl}
                  alt={`card ${active + 1}`}
                  style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4 }}
                />
              ) : (
                <video
                  src={card.mediaPreviewUrl}
                  muted
                  loop
                  autoPlay
                  playsInline
                  style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4 }}
                />
              )
            ) : (
              <Box h={100} bg={isDark ? 'dark.4' : 'gray.2'} style={{ borderRadius: 4 }}>
                <Text size="xs" c="dimmed" ta="center" mt={40}>
                  [{card.headerFormat.toLowerCase()} card]
                </Text>
              </Box>
            )}
            {card.bodyText && (
              <Text size="xs" mt={4}>
                {card.bodyText}
              </Text>
            )}
            {(card.buttons ?? []).map((b, i) => (
              <Box
                key={i}
                mt={4}
                p={4}
                ta="center"
                style={(t) => ({
                  borderRadius: 4,
                  border: `1px solid ${isDark ? t.colors.dark[3] : '#eee'}`,
                  color: t.colors.blue[6],
                  fontSize: 12,
                })}
              >
                {b.text || b.type}
              </Box>
            ))}
          </Box>
        </Box>

        <ActionIcon
          size="sm"
          variant="light"
          radius="xl"
          disabled={active === cards.length - 1}
          onClick={() => go(1)}
          aria-label="Next card"
        >
          <IconChevronRight size={14} />
        </ActionIcon>
      </Group>

      <Group gap={4} justify="center" mt={4}>
        {cards.map((_, i) => (
          <Box
            key={i}
            onClick={() => onActiveChange(i)}
            style={(t) => ({
              width: 7,
              height: 7,
              borderRadius: '50%',
              cursor: 'pointer',
              background: i === active ? t.colors.blue[6] : isDark ? t.colors.dark[3] : '#ccc',
            })}
          />
        ))}
        <Text size="xs" c="dimmed" ml={6}>
          {active + 1} of {cards.length}
        </Text>
      </Group>
    </Box>
  );
}

function authBodyPreview(_t: PreviewTemplate): string {
  return 'Your verification code is 123-456.';
}

function substitute(
  text: string,
  template: PreviewTemplate,
  opts?: { headerOnly?: boolean },
): string {
  if (!text) return '';
  let out = text;
  // Positional {{1}}..{{N}}
  if (template.parameterFormat !== 'NAMED' && template.bodyExamples) {
    out = out.replace(/\{\{(\d+)\}\}/g, (_, idx: string) => {
      const i = Number(idx) - 1;
      return template.bodyExamples?.[i] ?? `{{${idx}}}`;
    });
  } else if (
    template.parameterFormat === 'NAMED' &&
    template.bodyVariableNames &&
    template.bodyExamples
  ) {
    out = out.replace(/\{\{([a-z][a-z0-9_]*)\}\}/g, (_, name: string) => {
      const i = template.bodyVariableNames!.indexOf(name);
      return i >= 0 ? (template.bodyExamples?.[i] ?? `{{${name}}}`) : `{{${name}}}`;
    });
  }
  void opts;
  return out;
}

export function iconForButton(type: string) {
  switch (type) {
    case 'URL':
      return <IconExternalLink size={14} />;
    case 'PHONE_NUMBER':
      return <IconPhone size={14} />;
    case 'COPY_CODE':
      return <IconCopy size={14} />;
    case 'QUICK_REPLY':
      return <IconCircleCheck size={14} />;
    case 'FLOW':
      return <IconRobot size={14} />;
    default:
      return null;
  }
}

export function labelForUntitled(type: string): string {
  if (type === 'COPY_CODE') return 'Copy offer code';
  if (type === 'CATALOG') return 'View catalog';
  if (type === 'MPM') return 'View products';
  if (type === 'VOICE_CALL') return 'Voice call';
  return type;
}
